import type React from 'react'
import { applyCartAddons } from './cartAddons'

/**
 * Ad-platform pixels. Drop your IDs in here and both pixels start firing —
 * PageView on every page, ViewContent on the sales page and AddToCart on every
 * checkout click. InitiateCheckout and Purchase come from Shopify's checkout.
 * Leave an ID empty and that platform is skipped entirely.
 */
export const META_PIXEL_ID = '1745979086398479'
export const TIKTOK_PIXEL_ID = ''

type EventName = 'PageView' | 'ViewContent' | 'AddToCart' | 'InitiateCheckout' | 'Lead'

/**
 * Custom events for the conversion system. Standard pixel events above stay
 * untouched so existing Purchase / AddToCart / InitiateCheckout reporting keeps
 * working; these ride alongside as custom events for funnel analysis.
 */
export type CustomEventName =
  | 'bundle_1_selected'
  | 'bundle_3_selected'
  | 'bundle_5_selected'
  | 'business_pack_viewed'
  | 'business_pack_clicked'
  | 'objection_popup_viewed'
  | 'objection_popup_closed'
  | 'objection_battery_selected'
  | 'objection_visibility_selected'
  | 'objection_antifog_selected'
  | 'objection_lenses_selected'
  | 'objection_durability_selected'
  | 'objection_price_selected'
  | 'objection_none_want_it'
  | 'mini_funnel_battery_viewed'
  | 'mini_funnel_visibility_viewed'
  | 'mini_funnel_antifog_viewed'
  | 'mini_funnel_lenses_viewed'
  | 'mini_funnel_durability_viewed'
  | 'mini_funnel_price_viewed'
  | 'mini_funnel_cta_clicked'
  | 'price_split_offer_viewed'
  | 'price_split_clicked'
  | 'rescue_5_clicked'
  | 'rx_insert_updates_clicked'
  | 'mystery_gift_offer_viewed'
  | 'value_stack_viewed'
  | 'add_to_cart_single'
  | 'add_to_cart_full_shift'
  | 'add_to_cart_max_value'
  | 'add_to_cart_business'
  | 'two_x1_free_third_viewed'
  | 'two_x1_free_third_accepted'
  | 'two_x1_free_third_declined'
  | 'single_lifetime_995_viewed'
  | 'single_lifetime_995_accepted'
  | 'single_lifetime_995_declined'
  | 'single_to_trio_viewed'
  | 'single_to_trio_accepted'
  | 'single_to_trio_declined'
  | 'single_lifetime_795_viewed'
  | 'single_lifetime_795_accepted'
  | 'single_lifetime_795_declined'
  | 'single_lifetime_495_viewed'
  | 'single_lifetime_495_accepted'
  | 'single_lifetime_495_declined'
  | 'three_x1_lifetime_9990_viewed'
  | 'three_x1_lifetime_9990_accepted'
  | 'three_x1_lifetime_9990_declined'
  | 'three_x1_lifetime_8490_viewed'
  | 'three_x1_lifetime_8490_accepted'
  | 'three_x1_lifetime_8490_declined'
  | 'three_x1_lifetime_7990_viewed'
  | 'three_x1_lifetime_7990_accepted'
  | 'three_x1_lifetime_7990_declined'
  | 'single_free_shipping_retention_viewed'
  | 'single_free_shipping_retention_accepted'
  | 'single_free_shipping_retention_declined'
  | 'single_lifetime_shipping_rescue_viewed'
  | 'single_lifetime_shipping_rescue_accepted'
  | 'single_lifetime_shipping_rescue_declined'
  // Generic funnel events: every offer fires these with { offer_id, flow, source }.
  | 'upsell_offer_viewed'
  | 'upsell_offer_accepted'
  | 'upsell_offer_declined'
  | 'upsell_flow_completed'
  | 'upsell_confirmation_viewed'
  | 'crew_pricing_link_clicked'
  | 'want_free_shipping_clicked'
  | 'product_selected'
  | 'quantity_increased'
  | 'quantity_decreased'
  | 'lifetime_selected'
  | 'lifetime_declined'
  | 'pack_switch_suggested'
  | 'pack_switch_accepted'
  | 'objection_selected'
  | 'objection_just_looking_selected'
  | 'mini_funnel_viewed'
  | 'pack_recommendation_selected'
  | 'authenticity_page_visited'
  | 'batch_verification_attempted'
  | 'batch_verification_successful'
  | 'referral_page_visited'
  | 'referral_link_clicked'
  | 'activation_page_visited'
  | 'activation_started'
  | 'activation_completed'
  | 'activation_not_connected'
  // Build Your X1 configurator
  | 'build_your_x1_view'
  | 'essential_started'
  | 'primary_lens_clear'
  | 'primary_lens_amber'
  | 'primary_lens_smoke'
  | 'full_lens_added'
  | 'full_lens_removed'
  | 'hard_case_added'
  | 'hard_case_removed'
  | 'care_kit_added'
  | 'care_kit_removed'
  | 'rx_added'
  | 'rx_quantity'
  | 'one_year_protection_selected'
  | 'lifetime_protection_selected'
  | 'basic_protection_selected'
  | 'complete_offer_shown'
  | 'complete_offer_declined'
  | 'reviews_strip_clicked'
  | 'owner_center_visited'
  | 'quick_start_manual_opened'
  | 'owner_form_opened'
  | 'owner_form_started'
  | 'owner_form_submitted'
  | 'complete_offer_accepted'
  | 'complete_offer_reverted'
  | 'hard_case_downsell_shown'
  | 'hard_case_downsell_accepted'
  | 'hard_case_downsell_declined'
  | 'checkout_started'
  | 'builder_not_connected'
  | 'multipack_rx_quantity'
  | 'hero_offer_selected'
  | 'quick_start_step_viewed'
  | 'quick_start_page_visited'
  | 'track_order_page_visited'
  | 'track_order_submitted'
  | 'rx_section_clicked'
  | 'faq_opened'
  | 'nav_cta_clicked'
  | 'review_submitted'
  | 'discount_code_copied'

const tiktokNames: Record<EventName, string> = {
  PageView: 'Pageview',
  ViewContent: 'ViewContent',
  AddToCart: 'AddToCart',
  InitiateCheckout: 'InitiateCheckout',
  Lead: 'SubmitForm',
}

interface Win extends Window {
  fbq?: (...args: unknown[]) => void
  ttq?: { track: (name: string, data?: unknown) => void; page: () => void; load: (id: string) => void }
  dataLayer?: unknown[]
  _fbq?: unknown
}

export function trackEvent(name: EventName, data?: Record<string, unknown>) {
  if (typeof window === 'undefined') return
  const win = window as Win
  if (win.fbq) win.fbq('track', name, data)
  if (win.ttq) {
    if (name === 'PageView') win.ttq.page()
    else win.ttq.track(tiktokNames[name], data)
  }
  if (win.dataLayer) win.dataLayer.push({ event: name, ...data })
}

/**
 * Fires a named funnel event on every analytics destination that is present.
 * Safe to call when no pixel is configured: it simply does nothing.
 */
export function trackCustom(name: CustomEventName, data?: Record<string, unknown>) {
  if (typeof window === 'undefined') return
  const win = window as Win
  if (win.fbq) win.fbq('trackCustom', name, data)
  if (win.ttq) win.ttq.track(name, data)
  if (win.dataLayer) win.dataLayer.push({ event: name, ...data })
}

/** Per-pack analytics names, so bundle mix and AOV are measurable by offer. */
export const packEvents: Record<string, { selected: CustomEventName; addToCart: CustomEventName }> = {
  single: { selected: 'bundle_1_selected', addToCart: 'add_to_cart_single' },
  trio: { selected: 'bundle_3_selected', addToCart: 'add_to_cart_full_shift' },
  crew: { selected: 'bundle_5_selected', addToCart: 'add_to_cart_max_value' },
  business: { selected: 'business_pack_clicked', addToCart: 'add_to_cart_business' },
}

/**
 * Fires AddToCart on the click that sends the visitor to the Shopify cart.
 * The browser leaves the page right away, which can cancel the pixel request,
 * so the navigation waits a moment (max ~350ms) for the event to go out.
 */
export function trackShopifyAddToCart(
  event: React.MouseEvent<HTMLAnchorElement>,
  data: { content_name: string; content_ids: string[]; quantity: number; value: number },
) {
  const addons = applyCartAddons(event.currentTarget.href)
  trackEvent('AddToCart', {
    ...data,
    value: Number((data.value + addons.extraValue).toFixed(2)),
    num_items: data.quantity + addons.rx + addons.care,
    rx_quantity: addons.rx,
    care_kit_quantity: addons.care,
    content_type: 'product',
    currency: 'USD',
  })
  const href = addons.href
  // Let new-tab / modified clicks behave normally, with the add-ons already in the link.
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
    event.currentTarget.href = href
    return
  }
  event.preventDefault()
  window.setTimeout(() => {
    window.location.href = href
  }, 350)
}

/** Same as trackShopifyAddToCart, for routing that happens outside a link click (e.g. a popup button). */
export function goToShopifyCart(
  href: string,
  data: { content_name: string; content_ids: string[]; quantity: number; value: number },
) {
  const addons = applyCartAddons(href)
  trackEvent('AddToCart', {
    ...data,
    value: Number((data.value + addons.extraValue).toFixed(2)),
    num_items: data.quantity + addons.rx + addons.care,
    rx_quantity: addons.rx,
    care_kit_quantity: addons.care,
    content_type: 'product',
    currency: 'USD',
  })
  window.setTimeout(() => {
    window.location.href = addons.href
  }, 350)
}

function injectMeta(id: string) {
  if (document.getElementById('meta-pixel')) return
  const script = document.createElement('script')
  script.id = 'meta-pixel'
  script.innerHTML = `
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
    n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
    document,'script','https://connect.facebook.net/en_US/fbevents.js');
    fbq('init','${id}');fbq('track','PageView');
  `
  document.head.appendChild(script)
}

function injectTikTok(id: string) {
  if (document.getElementById('tiktok-pixel')) return
  const script = document.createElement('script')
  script.id = 'tiktok-pixel'
  script.innerHTML = `
    !function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];
    ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"];
    ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
    for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
    ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};
    ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js";
    ttq._i=ttq._i||{};ttq._i[e]=[];ttq._i[e]._u=r;ttq._t=ttq._t||{};ttq._t[e]=+new Date;
    ttq._o=ttq._o||{};ttq._o[e]=n||{};var o=d.createElement("script");o.type="text/javascript";
    o.async=!0;o.src=r+"?sdkid="+e+"&lib="+t;var a=d.getElementsByTagName("script")[0];
    a.parentNode.insertBefore(o,a)};
    ttq.load('${id}');ttq.page();}(window,document,'ttq');
  `
  document.head.appendChild(script)
}

export function initPixels() {
  if (typeof window === 'undefined') return
  if (META_PIXEL_ID) injectMeta(META_PIXEL_ID)
  if (TIKTOK_PIXEL_ID) injectTikTok(TIKTOK_PIXEL_ID)
}
